// Front Pages
import Home from "../pages/front/Home";

// User Pages
import UserPlaceOrder from "../pages/front/account/PlaceOrder.js";
import HowItWorks from "../pages/front/HowItWorks.js";
import Signin from "../pages/front/authentication/Signin.js";
import BookCreation from "../pages/front/account/BookCreation.js";
import UserLandingPage from "../pages/front/account/UserLandingPage.js";
import CoverEditorScreen from "../components/sections/front/User/CoverEditorScreen.js";
import ReviewBookContent from "../components/sections/front/User/ReviewBookContent.js";
import HomeTAC from "../components/sections/front/sections/HomeTAC.js";
import HomePAP from "../components/sections/front/sections/HomePAP.js";
import ShopifyCallback from "../pages/front/authentication/ShopifyCallback.js";
import EtsyClaim from "../pages/front/authentication/EtsyClaim.js";
import EtsyDone from "../pages/front/authentication/EtsyDone.js";
import GiftLink from "../pages/front/gift/GiftLink.js";
import GiftBooks from "../pages/front/gift/GiftBooks.js";
import GiftWelcome from "../pages/front/gift/GiftWelcome.js";
import GiftPlan from "../pages/front/gift/GiftPlan.js";
import GiftDone from "../pages/front/gift/GiftDone.js";
import ShopifyCart from "../pages/front/account/ShopifyCart.js";
import PdfOffer from "../pages/front/account/PdfOffer.js";
import Help from "../pages/front/account/Help.js";
import { Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";

/** A page in its own chunk, downloaded only when someone opens it. */
const lazyPage = (load) => {
  const Page = lazy(load);
  return () => (
    <Suspense fallback={null}>
      <Page />
    </Suspense>
  );
};

// Admin Pages: admin-only, so each is split out and never downloaded by
// customers (along with the editors, date pickers and drag-and-drop they use).
const AdminLogin = lazyPage(() => import("../pages/admin/signIn/AdminLogin.js"));
const ProfileComponent = lazyPage(() => import("../components/profile/Profile.js"));
const Dashboard = lazyPage(() => import("../pages/admin/Dashboard.js"));
const AdminAnalytics = lazyPage(() => import("../pages/admin/analytics/Analytics.js"));
const AdminInfluencers = lazyPage(() => import("../pages/admin/influencers/Influencers.js"));
const Testimonials = lazyPage(() => import("../pages/admin/Testimonials"));
const TestimonialDetails = lazyPage(() => import("../components/sections/admin/testimonials/TestimonialDetails.js"));
const Category = lazyPage(() => import("../pages/admin/Category"));
const AdminList = lazyPage(() => import("../pages/admin/admins/AdminList"));
const UserList = lazyPage(() => import("../pages/admin/users/UserList.js"));
const UserDetailsPage = lazyPage(() => import("../pages/admin/users/UserDetailsPage.js"));
const Faq = lazyPage(() => import("../pages/admin/faq/Faq.js"));
const Hiw = lazyPage(() => import("../pages/admin/hiw/Hiw.js"));
const SiteSettings = lazyPage(() => import("../pages/admin/Sitesetting"));
const EmailTemplate = lazyPage(() => import("../pages/admin/EmailTemplate"));
const Banners = lazyPage(() => import("../pages/admin/Banners"));
const ManageBanner = lazyPage(() => import("../components/sections/admin/banner/ManageBanner"));
const Plans = lazyPage(() => import("../pages/admin/plans/Plans.js"));
const Subscription = lazyPage(() => import("../pages/admin/subscription/Subscription.js"));
const PagesSections = lazyPage(() => import("../pages/admin/sections/PagesSections.js"));
const FooterSections = lazyPage(() => import("../pages/admin/sections/FooterSections.js"));
const ManagePagesSection = lazyPage(() => import("../components/sections/admin/sections/ManagePagesSection.js"));
const ManageFooterPagesSection = lazyPage(() => import("../components/sections/admin/sections/ManageFooterPagesSection.js"));
const Transaction = lazyPage(() => import("../pages/admin/transaction/Transaction.js"));
const Books = lazyPage(() => import("../pages/admin/books/Books.js"));
const AdminCreateBook = lazyPage(() => import("../pages/admin/books/AdminCreateBook.js"));
const PrintFulfillment = lazyPage(() => import("../pages/admin/PrintFulfillment.js"));
const Moderation = lazyPage(() => import("../pages/admin/Moderation.js"));
const GiftOrders = lazyPage(() => import("../pages/admin/GiftOrders.js"));
const CollectionTemplate = lazyPage(() => import("../pages/admin/CollectionTemplate/CollectionTemplate.js"));
const UserCollection = lazyPage(() => import("../pages/admin/UserCollection/UserCollection.js"));
const UserCollectioncovercontent = lazyPage(() => import("../pages/admin/UserCollection/UserCollectioncovercontent.js"));
const UserCollectionDetails = lazyPage(() => import("../pages/admin/UserCollection/UserCollectionDetails.js"));

// Linked from reminder emails only, so it's split out of the main bundle too.
const Unsubscribe = lazyPage(() => import("../pages/front/Unsubscribe.js"));

// The print partner's invoice page; one visitor a month, so it's split out too.
const PartnerInvoice = lazyPage(() => import("../pages/front/PartnerInvoice.js"));

/** Old URL; keeps bookmarks and external links working. */
const LegacyShopifyCartRedirect = () => (
  <Navigate to="/finalize-order" replace />
);

/** The old My Orders page (removed); My Books lists ordered books with their downloads. */
const LegacyMyOrdersRedirect = () => <Navigate to="/dashboard" replace />;

const AdminOrdersToBooksRedirect = () => <Navigate to="/admin/books" replace />;

export const frontRoutes = (data) => {
  return [
    {
      path: "/",
      title: "Home",
      component: Home,
      routeType: false,
      layout: "front",
    },
    {
      path: "/how-it-works",
      title: "How It Works",
      component: HowItWorks,
      routeType: false,
      layout: "front",
    },
    {
      path: "/terms-and-conditions",
      title: "Terms And Conditions",
      component: HomeTAC,
      routeType: false,
      layout: "front",
    },
    {
      path: "/privacy-policy",
      title: "Privacy And Policies",
      component: HomePAP,
      routeType: false,
      layout: "front",
    },
  ];
};

export const authRoutes = (data) => [
  {
    path: "/signin",
    title: "Signin",
    component: Signin,
    routeType: false,
    layout: "front",
  },
  {
    path: "/admin/signin",
    title: "Admin Sign in",
    component: AdminLogin,
    routeType: "auth",
    layout: "",
  },
  {
    path: "/auth/callback",
    title: "Verifying Authentication...",
    component: ShopifyCallback,
    routeType: false,
    layout: "front",
  },
  {
    // Public on purpose: an Etsy buyer has no account and no email (Etsy does
    // not release one), so this page IS their sign-in. It sits in authRoutes
    // rather than userRoutes so it is not behind the UserRoute guard.
    path: "/etsy",
    title: "Open your book",
    component: EtsyClaim,
    routeType: false,
    // Logo and copyright only: no menu, no links out. See EtsyLayout.
    layout: "etsy",
  },
  {
    // Public too: the session markers are cleared on submit, so a buyer landing
    // here is no longer "signed in" in any meaningful sense.
    path: "/etsy/done",
    title: "Your book is on its way",
    component: EtsyDone,
    routeType: false,
    // No site chrome: the buyer is done, and every link out of here (Home, How
    // It Works, My Books) is an invitation to wander back into a wizard that
    // no longer has a session. Same logo-only layout as /etsy.
    layout: "etsy",
  },
  {
    // Public: the button in a Shopify gift email. The signed link IS the
    // recipient's sign-in, so like /etsy it sits outside the UserRoute guard,
    // on the logo-only layout.
    path: "/gift",
    title: "Your gift",
    component: GiftLink,
    routeType: false,
    layout: "etsy",
  },
  {
    // Public: linked from unfinished-book reminder emails (signed ?u=&t= link,
    // or a bare link from older emails). Logo-only layout, nothing to wander into.
    path: "/unsubscribe",
    title: "Email preferences",
    component: Unsubscribe,
    routeType: false,
    layout: "etsy",
  },
  {
    // Public: the print partner's monthly invoice, from the signed link in
    // his email on the 1st. Logo-only layout.
    path: "/partner-invoice",
    title: "Invoice",
    component: PartnerInvoice,
    routeType: false,
    layout: "etsy",
  },
];

export const adminRoutes = (data) => [
  {
    path: "/user/profile",
    title: "Profile",
    layout: "admin",
    routeType: "ADMIN",
    component: ProfileComponent,
  },
  {
    path: "/admin",
    title: "dashboard",
    layout: "admin",
    routeType: "ADMIN",
    component: Dashboard,
  },
  {
    path: "/admin/dashboard",
    title: "dashboard",
    layout: "admin",
    routeType: "ADMIN",
    component: Dashboard,
  },
  {
    path: "/admin/analytics",
    title: "Analytics",
    layout: "admin",
    routeType: "ADMIN",
    component: AdminAnalytics,
  },
  {
    path: "/admin/influencers",
    title: "Influencers",
    layout: "admin",
    routeType: "ADMIN",
    component: AdminInfluencers,
  },
  {
    path: "/admin/testimonials",
    title: "testimonials",
    layout: "admin",
    routeType: "ADMIN",
    component: Testimonials,
  },
  {
    path: "/admin/testimonials/details/:id",
    title: "testimonials details",
    layout: "admin",
    routeType: "ADMIN",
    component: TestimonialDetails,
  },
  {
    path: "/admins",
    title: "Admin List",
    layout: "admin",
    routeType: "ADMIN",
    component: AdminList,
  },
  {
    path: "/admin/users",
    title: "User List",
    layout: "admin",
    routeType: "ADMIN",
    component: UserList,
  },
  {
    path: "/admin/users/details/:id",
    title: "User's details",
    layout: "admin",
    routeType: "ADMIN",
    component: UserDetailsPage,
  },
  {
    path: "/admin/collection-template",
    title: "Collection Templates",
    layout: "admin",
    routeType: "ADMIN",
    component: CollectionTemplate,
  },
  {
    path: "/admin/user-collections",
    title: "User's Collections",
    layout: "admin",
    routeType: "ADMIN",
    component: UserCollection,
  },
  {
    path: "/admin/user-collections/details/:id",
    title: "User's Collections Pages Details",
    layout: "admin",
    routeType: "ADMIN",
    component: UserCollectionDetails,
  },
  {
    path: "/admin/user-collections/cover-content/:id",
    title: "User's Collections cover content",
    layout: "admin",
    routeType: "ADMIN",
    component: UserCollectioncovercontent,
  },
  {
    path: "/admin/faq",
    title: "FAQ List",
    layout: "admin",
    routeType: "ADMIN",
    component: Faq,
  },
  {
    path: "/admin/how-it-works",
    title: "HIW List",
    layout: "admin",
    routeType: "ADMIN",
    component: Hiw,
  },
  {
    path: "/admin/site-settings",
    title: "settings",
    layout: "admin",
    routeType: "ADMIN",
    component: SiteSettings,
  },
  {
    path: "/admin/email-template",
    title: "Email Template",
    layout: "admin",
    routeType: "ADMIN",
    component: EmailTemplate,
  },
  {
    path: "/admin/:categoryType/categories",
    title: "categories",
    layout: "admin",
    routeType: "admin",
    component: Category,
  },
  {
    path: "/admin/pages",
    title: "sections",
    layout: "admin",
    routeType: "ADMIN",
    component: PagesSections,
  },
  {
    path: "/admin/footer-pages",
    title: "footer",
    layout: "admin",
    routeType: "ADMIN",
    component: FooterSections,
  },
  {
    path: "/admin/footerPages/manage",
    layout: "admin",
    routeType: "ADMIN",
    component: ManageFooterPagesSection,
  },
  {
    path: "/admin/pages/manage",
    layout: "admin",
    routeType: "ADMIN",
    component: ManagePagesSection,
  },
  {
    path: "/admin/banners",
    title: "banners",
    layout: "admin",
    routeType: "ADMIN",
    component: Banners,
  },
  {
    path: "/admin/banners/manage",
    layout: "admin",
    routeType: "ADMIN",
    component: ManageBanner,
  },
  {
    path: "/admin/plans",
    title: "plans",
    layout: "admin",
    routeType: "ADMIN",
    component: Plans,
  },
  {
    path: "/admin/subscription",
    title: "subscription",
    layout: "admin",
    routeType: "ADMIN",
    component: Subscription,
  },
  {
    path: "/admin/transaction",
    title: "Transaction",
    layout: "admin",
    routeType: "ADMIN",
    component: Transaction,
  },
  {
    path: "/admin/books",
    title: "Books",
    layout: "admin",
    routeType: "ADMIN",
    component: Books,
  },
  {
    path: "/admin/books/create",
    title: "Create a Book",
    layout: "admin",
    routeType: "ADMIN",
    component: AdminCreateBook,
  },
  {
    path: "/admin/orders",
    title: "Books",
    layout: "admin",
    routeType: "ADMIN",
    component: AdminOrdersToBooksRedirect,
  },
  {
    path: "/admin/print-fulfillment",
    title: "Print fulfillment",
    layout: "admin",
    routeType: "ADMIN",
    component: PrintFulfillment,
  },
  {
    path: "/admin/moderation",
    title: "Moderation",
    layout: "admin",
    routeType: "ADMIN",
    component: Moderation,
  },
  {
    path: "/admin/gift-orders",
    title: "Gift orders",
    layout: "admin",
    routeType: "ADMIN",
    component: GiftOrders,
  },
];

export const userRoutes = (data) => [
  {
    path: "/create-book",
    title: "Book Creation",
    component: BookCreation,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/dashboard",
    title: "Dashboard",
    component: UserLandingPage,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/create",
    title: "Place Order",
    component: UserPlaceOrder,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/cover-image",
    title: "Cover Image",
    component: CoverEditorScreen,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/review",
    title: "Review Book",
    component: ReviewBookContent,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/finalize-order",
    title: "Finalize Order",
    component: ShopifyCart,
    routeType: "USER",
    layout: "user",
  },
  {
    // PDF downsell link in Shopify's abandoned-checkout email: opens Review order with the
    // last checkout's books as PDF only.
    path: "/pdf-offer",
    title: "Finalize Order",
    component: PdfOffer,
    routeType: "USER",
    layout: "user",
  },
  {
    // The menu's Help (docs/design/HANDOFF.md: Help, TabHelp, DeskHelp).
    path: "/help",
    title: "Help",
    component: Help,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/shopify-cart",
    title: "Finalize Order",
    component: LegacyShopifyCartRedirect,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/my-orders",
    title: "My Books",
    component: LegacyMyOrdersRedirect,
    routeType: "USER",
    layout: "user",
  },
  // Shopify gift recipients: the welcome, the copy split, their books, address, send.
  {
    path: "/gift/welcome",
    title: "Your gift",
    component: GiftWelcome,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/gift/plan",
    title: "Split your books",
    component: GiftPlan,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/gift/books",
    title: "Your gift",
    component: GiftBooks,
    routeType: "USER",
    layout: "user",
  },
  {
    path: "/gift/done",
    title: "Your books are on their way",
    component: GiftDone,
    routeType: "USER",
    layout: "user",
  }
];

export const routerData = (data) => {
  return [
    ...userRoutes(data),
    ...adminRoutes(data),
    ...authRoutes(data),
    ...frontRoutes(data),
  ];
};
